import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Camera, Copy, Download, ExternalLink, Loader2, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_PATHS = [
  { label: 'Home (EN)', path: '/' },
  { label: 'Home (PT)', path: '/pt' },
  { label: 'Advisory (EN)', path: '/advisory' },
  { label: 'Consultoria (PT)', path: '/pt/advisory' },
  { label: 'Services (EN)', path: '/services' },
  { label: 'Serviços (PT)', path: '/pt/services' },
  { label: 'Regions (EN)', path: '/regions' },
  { label: 'Regiões (PT)', path: '/pt/regions' },
  { label: 'International (EN)', path: '/international' },
  { label: 'International (PT)', path: '/pt/international' },
  { label: 'Journal (EN)', path: '/journal' },
  { label: 'Journal (PT)', path: '/pt/journal' },
  { label: 'Contact (EN)', path: '/contact' },
  { label: 'Contacto (PT)', path: '/pt/contact' },
];

const SnapshotAdmin = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [path, setPath] = useState('/pt/advisory');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin/login');
  }, [authLoading, isAuthenticated, navigate]);

  const snapshotUrl = useMemo(
    () => `${BACKEND_URL}/api/snapshot/page?path=${encodeURIComponent(path)}`,
    [path]
  );
  const plainUrl = `${snapshotUrl}&plain=1`;

  const fetchSnapshot = async () => {
    setLoading(true);
    setError('');
    setHtml('');
    setCopied(false);
    try {
      const res = await fetch(plainUrl);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} — ${txt.slice(0, 200)}`);
      }
      const body = await res.text();
      setHtml(body);
    } catch (e) {
      setError(e.message || 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(html);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      setError('Não consegui copiar — selecione manualmente o texto.');
    }
  };

  const download = () => {
    const safeName = path.replace(/\//g, '_').replace(/^_|_$/g, '') || 'home';
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `snapshot_${safeName}.html`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (authLoading) return null;
  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-brand-soft">
      <div className="bg-white border-b border-brand-accent">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Voltar
            </Button>
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-brand-primary" />
              <h1 className="font-serif text-xl text-brand-text">Snapshot de Página</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-6xl">
        {/* Intro */}
        <Card className="mb-6">
          <CardContent className="pt-6 text-sm text-brand-text/80 leading-relaxed">
            <p className="mb-2">
              <strong>Para que serve.</strong> Esta ferramenta captura o HTML completamente renderizado de
              qualquer página do site (com toda a copy, layout e classes Tailwind aplicadas) e devolve-o
              como texto. Cola no Claude, ChatGPT ou qualquer ferramenta de revisão para receber feedback
              detalhado sobre tipografia, espaçamento, copy ou acessibilidade.
            </p>
            <p className="text-xs text-brand-muted">
              Endpoint: <code className="bg-brand-soft px-1.5 py-0.5">/api/snapshot/page?path=…</code> ·
              Render headless via Chromium · ~6 s por página.
            </p>
          </CardContent>
        </Card>

        {/* Path picker */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Escolher página</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {QUICK_PATHS.map((q) => (
                <button
                  key={q.path}
                  onClick={() => setPath(q.path)}
                  className={`text-left text-xs px-3 py-2 border transition-colors ${
                    path === q.path
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-white text-brand-text border-brand-accent hover:bg-brand-soft'
                  }`}
                  data-testid={`quick-${q.path}`}
                >
                  <div className="font-medium">{q.label}</div>
                  <div className="opacity-70">{q.path}</div>
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-brand-accent/30">
              <Input
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder="/pt/advisory"
                className="font-mono text-sm"
                data-testid="path-input"
              />
              <Button
                onClick={fetchSnapshot}
                disabled={loading}
                className="bg-brand-primary text-white hover:bg-brand-primary/90"
                data-testid="capture-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> A capturar…
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4 mr-2" /> Capturar
                  </>
                )}
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 text-xs">
              <a
                href={snapshotUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-brand-primary hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ver renderizado (nova aba)
              </a>
              <a
                href={plainUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-brand-primary hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ver como texto/plain
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Result */}
        {error && (
          <Card className="mb-6 border-red-300">
            <CardContent className="pt-6 text-sm text-red-700">{error}</CardContent>
          </Card>
        )}

        {html && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">
                Resultado <span className="text-xs text-brand-muted ml-2">{(html.length / 1024).toFixed(1)} KB</span>
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copy} data-testid="copy-button">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1.5 text-green-600" /> Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1.5" /> Copiar
                    </>
                  )}
                </Button>
                <Button size="sm" variant="outline" onClick={download} data-testid="download-button">
                  <Download className="w-4 h-4 mr-1.5" /> Download .html
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <textarea
                readOnly
                value={html}
                className="w-full h-96 font-mono text-xs p-3 bg-brand-soft border border-brand-accent resize-y"
                data-testid="html-output"
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SnapshotAdmin;
